"use client";

import { useEffect, useRef } from "react";

const STRINGS = [
  { note: "E", freq: 329.63, color: "#E84040", c: 0.48, damp: 0.003 },
  { note: "B", freq: 246.94, color: "#3B7FE8", c: 0.43, damp: 0.0035 },
  { note: "G", freq: 196.00, color: "#E8C547", c: 0.38, damp: 0.004 },
  { note: "D", freq: 146.83, color: "#8B5CF6", c: 0.33, damp: 0.0045 },
  { note: "A", freq: 110.00, color: "#40C070", c: 0.29, damp: 0.005 },
  { note: "E", freq:  82.41, color: "#EC4899", c: 0.25, damp: 0.006 },
];

const N = 180;

export function PlayableStrings() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hintRef   = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

    let W = 0, H = 0;
    let audioCtx: AudioContext | null = null;
    let firstInteraction = false;

    // ── Resize ──────────────────────────────────────────────────────────────
    // cv is always HTMLCanvasElement here — TypeScript doesn't narrow into closures
    const cv = canvas as HTMLCanvasElement;

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      W = cv.offsetWidth;
      H = cv.offsetHeight;
      cv.width  = W * dpr;
      cv.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(cv);

    // ── Physics ──────────────────────────────────────────────────────────────
    const y    = STRINGS.map(() => new Float64Array(N));
    const yOld = STRINGS.map(() => new Float64Array(N));

    function stringY(si: number) {
      return (H / (STRINGS.length + 1)) * (si + 1);
    }

    function pluck(si: number, xi: number, amp: number, hw = 14) {
      const ci = Math.max(1, Math.min(N - 2, xi));
      for (let i = Math.max(1, ci - hw); i < Math.min(N - 1, ci + hw); i++) {
        y[si][i] += amp * (1 - Math.abs(i - ci) / hw);
      }
    }

    function update() {
      STRINGS.forEach((s, si) => {
        const c2  = s.c * s.c;
        const buf = new Float64Array(N);
        for (let i = 1; i < N - 1; i++) {
          const wave = 2 * y[si][i] - yOld[si][i]
            + c2 * (y[si][i + 1] - 2 * y[si][i] + y[si][i - 1]);
          // velocity damping
          buf[i] = wave - s.damp * (y[si][i] - yOld[si][i]);
        }
        yOld[si].set(y[si]);
        y[si].set(buf);
      });
    }

    // ── Audio ────────────────────────────────────────────────────────────────
    function getAC() {
      if (!audioCtx) audioCtx = new AudioContext();
      if (audioCtx.state === "suspended") void audioCtx.resume();
      return audioCtx;
    }

    function playNote(freq: number, velocity: number) {
      const ac  = getAC();
      const now = ac.currentTime;
      const dur = 2.2;

      // White-noise burst
      const len = Math.ceil(ac.sampleRate * 0.06);
      const buf = ac.createBuffer(1, len, ac.sampleRate);
      const d   = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;

      const src = ac.createBufferSource();
      src.buffer = buf;

      const out = ac.createGain();
      out.gain.setValueAtTime(Math.min(velocity * 0.45, 0.45), now);
      out.gain.exponentialRampToValueAtTime(0.001, now + dur);
      out.connect(ac.destination);

      // Harmonics 1–3
      [1, 2, 3].forEach((h) => {
        const bp = ac.createBiquadFilter();
        bp.type = "bandpass";
        bp.frequency.value = freq * h;
        bp.Q.value = 50 / h;
        const hg = ac.createGain();
        hg.gain.value = 1 / (h * 1.5);
        src.connect(bp);
        bp.connect(hg);
        hg.connect(out);
      });

      src.start(now);
      src.stop(now + 0.1);
    }

    // ── Interaction ──────────────────────────────────────────────────────────
    let mouseX = -1, mouseY = -1, isDown = false;

    function nearestString(my: number) {
      const zone = H / (STRINGS.length + 1) * 0.55;
      return STRINGS.findIndex((_, si) => Math.abs(my - stringY(si)) < zone);
    }

    function interact(mx: number, my: number, ampScale: number) {
      const xi = Math.round((mx / W) * N);
      STRINGS.forEach((_, si) => {
        const dy   = my - stringY(si);
        const zone = H / (STRINGS.length + 1) * 0.55;
        if (Math.abs(dy) < zone) {
          pluck(si, xi, dy * ampScale, 11);
        }
      });
    }

    function markInteracted() {
      if (!firstInteraction) {
        firstInteraction = true;
        if (hintRef.current) hintRef.current.style.opacity = "0";
      }
    }

    function onMouseMove(e: MouseEvent) {
      const r = cv.getBoundingClientRect();
      mouseX = e.clientX - r.left;
      mouseY = e.clientY - r.top;
      if (isDown) interact(mouseX, mouseY, 0.13);
    }

    function onMouseDown(e: MouseEvent) {
      isDown = true;
      const r  = cv.getBoundingClientRect();
      const mx = e.clientX - r.left;
      const my = e.clientY - r.top;
      const si = nearestString(my);
      if (si >= 0) {
        const dy  = my - stringY(si);
        const xi  = Math.round((mx / W) * N);
        const amp = Math.sign(dy) * (Math.abs(dy) * 0.5 + 9);
        pluck(si, xi, amp, 14);
        markInteracted();
        playNote(STRINGS[si].freq, Math.min(Math.abs(amp) / 22, 1));
      }
    }

    function onMouseUp() { isDown = false; }

    function onTouchStart(e: TouchEvent) {
      const r  = cv.getBoundingClientRect();
      const t  = e.touches[0];
      const mx = t.clientX - r.left;
      const my = t.clientY - r.top;
      const si = nearestString(my);
      if (si >= 0) {
        const dy  = my - stringY(si);
        const xi  = Math.round((mx / W) * N);
        const amp = Math.sign(dy) * (Math.abs(dy) * 0.5 + 9);
        pluck(si, xi, amp, 14);
        markInteracted();
        playNote(STRINGS[si].freq, Math.min(Math.abs(amp) / 22, 1));
      }
    }

    function onTouchMove(e: TouchEvent) {
      const r = cv.getBoundingClientRect();
      const t = e.touches[0];
      interact(t.clientX - r.left, t.clientY - r.top, 0.15);
    }

    cv.addEventListener("mousemove", onMouseMove);
    cv.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup",  onMouseUp);
    // Passive: the canvas never cancels a touch, so vertical scrolling over it
    // behaves exactly like scrolling over any other section (touch-action: pan-y).
    cv.addEventListener("touchstart", onTouchStart, { passive: true });
    cv.addEventListener("touchmove",  onTouchMove,  { passive: true });

    // ── Draw ─────────────────────────────────────────────────────────────────
    function draw() {
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "#0A0A0A";
      ctx.fillRect(0, 0, W, H);

      STRINGS.forEach((s, si) => {
        const sy   = stringY(si);
        const near = mouseY > 0 && Math.abs(mouseY - sy) < H / (STRINGS.length + 1) * 0.6;

        // End-pins
        [0, W].forEach((px) => {
          ctx.save();
          ctx.fillStyle = "#2A2A2A";
          ctx.beginPath();
          ctx.arc(px, sy, 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        });

        // Glow pass
        ctx.save();
        ctx.beginPath();
        for (let i = 0; i < N; i++) {
          const x  = (i / (N - 1)) * W;
          const yy = sy + y[si][i];
          if (i === 0) {
            ctx.moveTo(x, yy);
          } else {
            ctx.lineTo(x, yy);
          }
        }
        ctx.strokeStyle = s.color;
        ctx.lineWidth   = near ? 7 : 4;
        ctx.shadowColor = s.color;
        ctx.shadowBlur  = near ? 28 : 16;
        ctx.globalAlpha = near ? 0.5 : 0.22;
        ctx.stroke();
        ctx.restore();

        // Main string
        ctx.save();
        ctx.beginPath();
        for (let i = 0; i < N; i++) {
          const x  = (i / (N - 1)) * W;
          const yy = sy + y[si][i];
          if (i === 0) {
            ctx.moveTo(x, yy);
          } else {
            ctx.lineTo(x, yy);
          }
        }
        ctx.strokeStyle = s.color;
        ctx.lineWidth   = near ? 2.5 : 1.5;
        ctx.shadowColor = s.color;
        ctx.shadowBlur  = near ? 10 : 5;
        ctx.globalAlpha = near ? 1 : 0.65;
        ctx.stroke();
        ctx.restore();

        // Specular highlight
        ctx.save();
        ctx.beginPath();
        for (let i = 0; i < N; i++) {
          const x  = (i / (N - 1)) * W;
          const yy = sy + y[si][i] - (near ? 1.2 : 0.7);
          if (i === 0) {
            ctx.moveTo(x, yy);
          } else {
            ctx.lineTo(x, yy);
          }
        }
        ctx.strokeStyle = "rgba(255,255,255,0.28)";
        ctx.lineWidth   = near ? 0.9 : 0.4;
        ctx.globalAlpha = near ? 0.55 : 0.18;
        ctx.stroke();
        ctx.restore();

        // Note label
        ctx.save();
        ctx.fillStyle      = s.color;
        ctx.globalAlpha    = near ? 0.95 : 0.35;
        ctx.font           = `bold ${near ? 13 : 11}px monospace`;
        ctx.textAlign      = "left";
        ctx.textBaseline   = "middle";
        ctx.shadowColor    = s.color;
        ctx.shadowBlur     = near ? 8 : 0;
        ctx.fillText(s.note, 14, sy);
        ctx.restore();
      });
    }

    // ── Loop ─────────────────────────────────────────────────────────────────
    // The simulation only runs while the canvas is actually on screen. Off
    // screen (or with reduced motion requested) it draws one static frame and
    // stops, instead of burning a rAF + six shadow-blurred strokes forever.
    let rafId = 0;
    let running = false;

    function loop() {
      update();
      draw();
      rafId = requestAnimationFrame(loop);
    }

    function start() {
      if (running || reducedMotion) return;
      running = true;
      rafId = requestAnimationFrame(loop);
    }

    function stop() {
      if (!running) return;
      running = false;
      cancelAnimationFrame(rafId);
    }

    const demoTimers: ReturnType<typeof setTimeout>[] = [];
    let onScreen = false;

    function sync() {
      if (onScreen && !document.hidden) start();
      else stop();
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting;
        sync();
      },
      { threshold: 0 }
    );
    io.observe(cv);
    document.addEventListener("visibilitychange", sync);

    if (reducedMotion) {
      // Static frame: the strings are still drawn, they just don't move.
      draw();
    } else {
      // Auto-demo plucks so it's alive on load
      demoTimers.push(
        setTimeout(() => pluck(2, Math.round(N * 0.40), -22, 16), 700),
        setTimeout(() => pluck(0, Math.round(N * 0.58), 15, 12), 1300),
        setTimeout(() => pluck(4, Math.round(N * 0.33), -14, 15), 1900),
        setTimeout(() => pluck(1, Math.round(N * 0.68), 18, 13), 2600),
        setTimeout(() => pluck(5, Math.round(N * 0.48), -10, 14), 3200),
      );
    }

    return () => {
      stop();
      io.disconnect();
      document.removeEventListener("visibilitychange", sync);
      demoTimers.forEach(clearTimeout);
      ro.disconnect();
      window.removeEventListener("mouseup", onMouseUp);
      cv.removeEventListener("mousemove", onMouseMove);
      cv.removeEventListener("mousedown", onMouseDown);
      cv.removeEventListener("touchstart", onTouchStart);
      cv.removeEventListener("touchmove",  onTouchMove);
      audioCtx?.close();
    };
  }, []);

  return (
    <section
      style={{
        backgroundColor: "#0A0A0A",
        borderTop: "1px solid #1A1A1A",
        borderBottom: "1px solid #1A1A1A",
      }}
    >
      <div className="container-main pt-14 pb-6 text-center">
        <p
          className="text-xs font-semibold uppercase tracking-[0.2em] mb-3"
          style={{ color: "#8A8A8A" }}
        >
          Interactive
        </p>
        <h2
          className="text-3xl sm:text-4xl font-black"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Six strings, six colors.
        </h2>
        <p className="text-sm mt-2" style={{ color: "#8A8A8A" }}>
          Click or drag across the strings below.
        </p>
        <p className="text-xs mt-3 max-w-md mx-auto" style={{ color: "#6A6A6A" }}>
          Illustration only. The motion and the tones are generated in your
          browser — this is not a recording of Aurora strings and does not
          demonstrate how they sound or feel.
        </p>
      </div>

      <div className="relative">
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: "clamp(300px, 40vh, 420px)",
            display: "block",
            cursor: "crosshair",
            touchAction: "pan-y",
          }}
        />
        <div
          ref={hintRef}
          style={{
            position: "absolute",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            color: "#2A2A2A",
            fontSize: 10,
            fontFamily: "monospace",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            transition: "opacity 0.6s ease",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          click or drag
        </div>
      </div>

      <div className="container-main pb-10 pt-5 flex justify-center gap-5 sm:gap-8 flex-wrap">
        {STRINGS.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <span
              style={{
                display: "inline-block",
                width: 24,
                height: 1.5 + i * 0.3,
                backgroundColor: s.color,
                borderRadius: 9999,
                boxShadow: `0 0 5px ${s.color}`,
              }}
            />
            <span className="text-xs font-mono" style={{ color: "#8A8A8A" }}>
              {s.note}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
